import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { collection, addDoc, getDocs, doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebaseConfig';
import { useAuth } from '../context/AuthContext';
import { getReportItems, getSiteSystems, LEAD_ITEMS, numberRepairPhotos, repairCaption } from '../reportItems';
import {
  loadReport, saveSlot, saveSiteSystems, emptySlot,
  combinedFindings, isNotApplicable, isConfirmed, slotProblems,
  setPrimaryPhoto, addExtraPhoto, removePhotoAt, setFindingsAt, setStatus, setLineComment,
  setNotApplicable, confirmSlot, reopenSlot, setDetail, slotHasRepair, autoFindings, NO_DEFICIENCY_TEXT,
} from '../reportSlots';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';
import '../App.css';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const photoUrl = (p) => (p ? (p.thumbUrl || p.fullUrl || p.url) : null);
const isPass   = (v) => v === 'good' || v === 'yes' || v === 'na';
const LONG_FINDINGS = 300;   // about three lines in the photo report box

// ─── Page ─────────────────────────────────────────────────────────────────────

const PhotoManager = () => {
  const { projectId }   = useParams();
  const { currentUser } = useAuth();
  const location        = useLocation();
  const navigate        = useNavigate();

  const [project, setProject]         = useState(null);
  const [photos, setPhotos]           = useState([]);
  const [slots, setSlots]             = useState({});
  const [siteSystems, setSiteSystems] = useState({});
  const [screen, setScreen]           = useState('slots');   // 'checklist' | 'slots'
  const [drafts, setDrafts]           = useState({});        // text being typed, saved on blur
  const [loading, setLoading]         = useState(true);
  const [saveError, setSaveError]     = useState('');
  const [uploading, setUploading]     = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [picker, setPicker]           = useState(null);      // { itemKey, mode: 'primary' | 'extra', label }
  const [confirmMove, setConfirmMove] = useState(null);      // { photo, fromLabel }
  const [highlight, setHighlight]     = useState('');        // slot jumped to from the Electrical Form
  const [showOther, setShowOther]     = useState(false);     // include structural photos in the picker

  const county  = project?.county;
  const items   = useMemo(() => (project ? getReportItems(county) : []), [project, county]);
  const systems = useMemo(() => (project ? getSiteSystems(county) : []), [project, county]);
  const allItems = useMemo(() => [...LEAD_ITEMS, ...items], [items]);

  // ─── Load ───────────────────────────────────────────────────────────────

  useEffect(() => {
    const load = async () => {
      try {
        const projectDoc = await getDoc(doc(db, 'projects', projectId));
        const proj = projectDoc.exists() ? projectDoc.data() : null;
        setProject(proj);

        const snap = await getDocs(collection(db, 'projects', projectId, 'photos'));
        setPhotos(snap.docs.map(d => ({ id: d.id, ...d.data() })));

        const report = await loadReport(projectId, proj?.county);
        setSlots(report.slots);
        setSiteSystems(report.siteSystems);

        const needed = getSiteSystems(proj?.county);
        const unanswered = needed.some(s => typeof report.siteSystems?.[s.key]?.present !== 'boolean');
        setScreen(unanswered ? 'checklist' : 'slots');
      } catch (err) {
        console.error('Error loading Photo Manager:', err);
        setSaveError('Could not load this project. Refresh to try again.');
      }
      setLoading(false);
    };
    load();
  }, [projectId]);

  // ─── Jump to a slot (from "Edit in Photo Manager" on the Electrical Form) ──

  useEffect(() => {
    if (loading || screen !== 'slots') return;
    const target = (location.hash || '').replace('#', '');
    if (!target) return;
    const el = document.getElementById(target);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setHighlight(target);
    const t = setTimeout(() => setHighlight(''), 2500);
    return () => clearTimeout(t);
  }, [loading, screen, location.hash]);

  // ─── Lookups ────────────────────────────────────────────────────────────

  const photoById = (id) => photos.find(p => p.id === id) || null;
  const slotOf    = (key) => slots[key] || emptySlot();

  // Repair photos are numbered after the last form item (PHOTO-29, 30 …)
  const repairLabels = useMemo(() => (project ? numberRepairPhotos(county, slots) : {}), [project, county, slots]);
  const repairLabel  = (itemKey, extraIndex) => repairLabels[itemKey]?.[extraIndex] || 'PHOTO-??';

  const usage = useMemo(() => {
    const map = {};
    allItems.forEach(it => {
      const s = slots[it.key];
      if (!s) return;
      s.photos.forEach((p, i) => {
        if (p.photoId) {
          const rl = (repairLabels[it.key] || [])[i - 1] || 'PHOTO-??';
          map[p.photoId] = { itemKey: it.key, index: i, label: i === 0 ? it.photoLabel : `${rl} ${repairCaption(it)}` };
        }
      });
    });
    return map;
  }, [slots, allItems, repairLabels]);

  // Electrical pool: everything not tagged structural (photos from before tagging count as electrical)
  const isStructuralPhoto = (p) => p?.discipline === 'structural';
  const pool = photos.filter(p => showOther || !isStructuralPhoto(p));
  const unusedCount = photos.filter(p => !isStructuralPhoto(p) && !usage[p.id]).length;

  const retag = async (photo, discipline) => {
    try {
      await updateDoc(doc(db, 'projects', projectId, 'photos', photo.id), { discipline });
      setPhotos(prev => prev.map(p => (p.id === photo.id ? { ...p, discipline } : p)));
    } catch (err) {
      console.error('Re-tag error:', err);
      setSaveError('Could not change the photo’s discipline. Try again.');
    }
  };

  // ─── Save ───────────────────────────────────────────────────────────────

  const commit = async (itemKey, nextSlot) => {
    setSlots(prev => ({ ...prev, [itemKey]: nextSlot }));
    try {
      await saveSlot(projectId, itemKey, nextSlot, currentUser.uid);
      setSaveError('');
    } catch (err) {
      console.error('Slot save error:', err);
      setSaveError('Last change did not save. Check your connection and try again.');
    }
  };

  // ─── Site systems ───────────────────────────────────────────────────────

  const sysAnswer = (key) => siteSystems?.[key] || {};

  const answerSystem = (sys, present) => {
    const prev = sysAnswer(sys.key);
    const reason = prev.reason !== undefined ? prev.reason : (sys.defaultReason || '');
    setSiteSystems(s => ({ ...s, [sys.key]: { present, reason } }));
  };

  const typeSystemReason = (sys, text) =>
    setSiteSystems(s => ({ ...s, [sys.key]: { ...sysAnswer(sys.key), reason: text } }));

  const unansweredCount = systems.filter(s => typeof sysAnswer(s.key).present !== 'boolean').length;
  const missingReason   = systems.filter(s => sysAnswer(s.key).present === false && !(sysAnswer(s.key).reason || '').trim());

  const applyChecklist = async () => {
    try {
      await saveSiteSystems(projectId, siteSystems);
      for (const sys of systems) {
        const a = sysAnswer(sys.key);
        const item = items.find(i => i.key === sys.itemKey);
        const current = slotOf(sys.itemKey);
        if (a.present === false) {
          // Engineer's own answer → slot is N/A and confirmed
          await commit(sys.itemKey, confirmSlot(setNotApplicable(current, a.reason, 'checklist'), currentUser.uid));
        } else if (a.present === true) {
          let next = current;
          if (current.naSource === 'checklist') next = reopenSlot(setNotApplicable(current, ''));
          if (item?.presetFindings && !(next.photos[0]?.findings || '').trim()) {
            next = setFindingsAt(next, 0, item.presetFindings);
          }
          if (next !== current) await commit(sys.itemKey, next);
        }
      }
      setScreen('slots');
      setSaveError('');
    } catch (err) {
      console.error('Checklist save error:', err);
      setSaveError('Site systems did not save. Check your connection and try again.');
    }
  };

  // ─── Photo placement ────────────────────────────────────────────────────

  const placePhoto = async (photoId, target) => {
    const used = usage[photoId];
    let current = slotOf(target.itemKey);
    if (used && !(used.itemKey === target.itemKey && target.mode === 'primary' && used.index === 0)) {
      if (used.itemKey === target.itemKey) {
        current = removePhotoAt(current, used.index);
      } else {
        await commit(used.itemKey, removePhotoAt(slotOf(used.itemKey), used.index));
      }
    }
    const next = target.mode === 'primary' ? setPrimaryPhoto(current, photoId) : addExtraPhoto(current, photoId);
    await commit(target.itemKey, next);
    setPicker(null);
    setConfirmMove(null);
  };

  const uploadToSlot = (file, target) => {
    if (!file) return;
    setUploading(true);
    setUploadProgress(0);
    const filename   = `${Date.now()}_${file.name}`;
    const storageRef = ref(storage, `projects/${projectId}/photos/${filename}`);
    const task       = uploadBytesResumable(storageRef, file);

    task.on('state_changed',
      snap => setUploadProgress(Math.round(snap.bytesTransferred / snap.totalBytes * 100)),
      err  => { console.error(err); setUploading(false); setSaveError(`Upload failed (${err.code || err.message}). Try again.`); },
      async () => {
        try {
          const url = await getDownloadURL(task.snapshot.ref);
          const docRef = await addDoc(collection(db, 'projects', projectId, 'photos'), {
            url, filename, caption: file.name, source: 'web-upload', discipline: 'electrical',
            uploadedBy: currentUser.uid, uploadedAt: serverTimestamp(),
          });
          setPhotos(prev => [...prev, { id: docRef.id, url, filename, caption: file.name }]);
          const current = slotOf(target.itemKey);
          const next = target.mode === 'primary' ? setPrimaryPhoto(current, docRef.id) : addExtraPhoto(current, docRef.id);
          await commit(target.itemKey, next);
        } catch (err) {
          console.error('Upload save error:', err);
          setSaveError('Photo uploaded but could not be placed. Use Choose photo to place it.');
        }
        setUploading(false);
        setUploadProgress(0);
      }
    );
  };

  // ─── Text fields (typed into drafts, saved on blur) ─────────────────────

  const draftVal = (k, saved) => (drafts[k] !== undefined ? drafts[k] : saved);
  const typeDraft = (k, text) => setDrafts(prev => ({ ...prev, [k]: text }));
  const dropDraft = (k) => setDrafts(prev => { const n = { ...prev }; delete n[k]; return n; });

  const saveFindings = async (itemKey, index, textOverride) => {
    const k = `${itemKey}:f${index}`;
    const text = textOverride !== undefined ? textOverride : drafts[k];
    if (text === undefined) return;
    const current = slotOf(itemKey);
    if ((current.photos[index]?.findings || '') !== text) await commit(itemKey, setFindingsAt(current, index, text));
    dropDraft(k);
  };

  const saveLineComment = async (itemKey, lineId) => {
    const k = `${itemKey}:c${lineId}`;
    const text = drafts[k];
    if (text === undefined) return;
    const current = slotOf(itemKey);
    if ((current.lineComments?.[lineId] || '') !== text) await commit(itemKey, setLineComment(current, lineId, text));
    dropDraft(k);
  };

  const saveNAReason = async (itemKey) => {
    const k = `${itemKey}:na`;
    const text = drafts[k];
    if (text === undefined) return;
    const current = slotOf(itemKey);
    await commit(itemKey, setNotApplicable(current, text.trim() || current.naReason, current.naSource || 'engineer'));
    dropDraft(k);
  };

  // ─── Progress ───────────────────────────────────────────────────────────

  const confirmedCount = allItems.filter(it => isConfirmed(slots[it.key]) && slotProblems(slots[it.key], it).length === 0).length;
  const aiCount        = allItems.filter(it => slots[it.key]?.review === 'ai').length;
  const openLabels     = allItems.filter(it => !isConfirmed(slots[it.key]) || slotProblems(slots[it.key], it).length > 0).map(it => it.photoLabel);

  // ─── Checklist screen ───────────────────────────────────────────────────

  const renderChecklist = () => (
    <section aria-label="Site systems" style={styles.card}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
        <h3 style={styles.cardTitle}>Site systems</h3>
        <p style={styles.cardText}>
          Answer each before the photo review opens. Systems marked not present print as N/A with the reason shown, and the AI skips them.
        </p>
      </div>

      {systems.map(sys => {
        const a = sysAnswer(sys.key);
        const reason = a.reason !== undefined ? a.reason : (sys.defaultReason || '');
        return (
          <div key={sys.key} style={styles.sysRow}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', flex: '1 1 320px', minWidth: 0 }}>
              <span style={styles.sysTitle}>{sys.photoLabel} — {sys.title}</span>
              {a.present === false && (
                <>
                  <label style={styles.smallLabel}>
                    N/A reason
                    <input style={styles.textInput} value={reason} onChange={e => typeSystemReason(sys, e.target.value)}
                      placeholder="Reason this system is not applicable" />
                  </label>
                  <span style={styles.printsLine}>Prints: Not applicable — {(reason || '…').replace(/\.$/, '')}.</span>
                </>
              )}
              {a.present === true && sys.key === 'thermography' && (
                <span style={styles.printsLine}>PHOTO-15 prints "Refer to separate thermography report." with no photo.</span>
              )}
            </div>
            <div role="group" aria-label={sys.title} style={styles.btnRow}>
              <button type="button" aria-pressed={a.present === true}
                style={a.present === true ? styles.choiceOnPass : styles.choiceOff}
                onClick={() => answerSystem(sys, true)}>Present</button>
              <button type="button" aria-pressed={a.present === false}
                style={a.present === false ? styles.choiceOnNa : styles.choiceOff}
                onClick={() => answerSystem(sys, false)}>Not present</button>
            </div>
          </div>
        );
      })}

      <div style={styles.checklistFoot}>
        {unansweredCount > 0 && <span style={styles.warnText}>{unansweredCount} still to answer</span>}
        {unansweredCount === 0 && missingReason.length > 0 && <span style={styles.warnText}>Enter an N/A reason for {missingReason.map(s => s.title).join(', ')}</span>}
        <button type="button"
          style={unansweredCount === 0 && missingReason.length === 0 ? styles.primaryBtnLg : styles.disabledBtnLg}
          disabled={unansweredCount > 0 || missingReason.length > 0}
          onClick={applyChecklist}>Open photo review</button>
      </div>
    </section>
  );

  // ─── Picker modal ───────────────────────────────────────────────────────

  const renderPicker = () => {
    const sorted = [...pool].sort((a, b) => (usage[a.id] ? 1 : 0) - (usage[b.id] ? 1 : 0));
    const close = () => { setPicker(null); setConfirmMove(null); };
    return (
      <div style={styles.modalOverlay} onClick={close}>
        <div style={styles.modalBox} onClick={e => e.stopPropagation()} role="dialog" aria-label="Choose photo">
          <div style={styles.modalHeader}>
            <span style={styles.modalTitle}>Choose photo — {picker.label}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <label style={styles.checkRow}>
                <input type="checkbox" checked={showOther} onChange={e => setShowOther(e.target.checked)} />
                <span>Show structural photos</span>
              </label>
              <button type="button" style={styles.modalClose} aria-label="Close" onClick={close}>✕</button>
            </div>
          </div>

          {confirmMove && (
            <div style={styles.confirmBanner}>
              <span>This photo is already used as <strong>{confirmMove.fromLabel}</strong>. Move it to {picker.label}?</span>
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                <button type="button" style={styles.confirmYes} onClick={() => placePhoto(confirmMove.photo.id, picker)}>Yes, move it</button>
                <button type="button" style={styles.confirmNo} onClick={() => setConfirmMove(null)}>Cancel</button>
              </div>
            </div>
          )}

          <div style={{ flex: '1 1 0', minHeight: 0, overflowY: 'auto' }}>
            {sorted.length === 0 ? (
              <div style={styles.modalEmpty}>
                <p>{photos.length ? 'No electrical photos in this project yet.' : 'No photos in this project yet.'}</p>
                <p style={{ fontSize: '0.8rem', marginTop: '0.5rem' }}>Take photos with the RecertAgent iOS app, or use Upload on the slot.</p>
              </div>
            ) : (
              <div style={styles.pickerGrid}>
                {sorted.map(p => {
                  const used = usage[p.id];
                  return (
                    <button key={p.id} type="button"
                      style={{ ...styles.pickerCard, borderColor: used ? '#b45309' : '#e5e7eb' }}
                      onClick={() => (used ? setConfirmMove({ photo: p, fromLabel: used.label }) : placePhoto(p.id, picker))}>
                      <img src={photoUrl(p)} alt={p.caption || p.filename || 'Field photo'} style={styles.pickerThumb}
                        onError={e => { e.target.style.display = 'none'; }} />
                      <span style={styles.pickerCaption}>
                        {used
                          ? <span style={{ color: '#92400e', fontWeight: 600 }}>Used as {used.label}</span>
                          : <span style={{ color: '#065f46', fontWeight: 600 }}>Not used yet</span>}
                        {(p.lineItemLabel || p.notes || p.caption) && (
                          <span style={{ color: '#4b5563' }}>{p.lineItemLabel || p.notes || p.caption}</span>
                        )}
                        <span style={{ color: '#4b5563' }}>
                          {isStructuralPhoto(p) ? 'Structural' : 'Electrical'} ·{' '}
                          <span role="button" tabIndex={0} style={styles.linkText}
                            onClick={e => { e.stopPropagation(); retag(p, isStructuralPhoto(p) ? 'electrical' : 'structural'); }}
                            onKeyDown={e => { if (e.key === 'Enter') { e.stopPropagation(); retag(p, isStructuralPhoto(p) ? 'electrical' : 'structural'); } }}>
                            Tag as {isStructuralPhoto(p) ? 'electrical' : 'structural'}
                          </span>
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ─── Slot pieces ────────────────────────────────────────────────────────

  const renderPhotoBox = ({ photo, label, target, onRemove }) => (
    photo ? (
      <div style={styles.photoBox}>
        <img src={photoUrl(photo)} alt={label} style={styles.thumbnail} />
        <div style={styles.btnRow}>
          <button type="button" style={styles.secondaryBtn} onClick={() => setPicker({ ...target, label })}>Change photo</button>
          <button type="button" style={styles.removeBtn} onClick={onRemove}>Remove</button>
        </div>
      </div>
    ) : (
      <div style={styles.emptyPhoto}>
        <div style={styles.btnRow}>
          <button type="button" style={styles.primaryBtn} onClick={() => setPicker({ ...target, label })}>Choose photo</button>
          <label style={styles.uploadLabel}>
            Upload
            <input type="file" accept="image/*" style={{ display: 'none' }}
              onChange={e => { uploadToSlot(e.target.files[0], target); e.target.value = ''; }} />
          </label>
        </div>
      </div>
    )
  );

  const renderFindings = (item, index, hasRepair) => {
    const k = `${item.key}:f${index}`;
    const value = draftVal(k, slotOf(item.key).photos[index]?.findings || '');
    return (
      <div style={styles.findingsWrap}>
        <label style={styles.findingsLabel} htmlFor={`f-${item.key}-${index}`}>Findings</label>
        <textarea id={`f-${item.key}-${index}`} rows={3}
          style={{ ...styles.findings, borderColor: index === 0 && hasRepair && (!value.trim() || /no deficienc(y|ies) observed/i.test(value)) ? '#b91c1c' : '#c3cad5' }}
          value={value}
          onChange={e => typeDraft(k, e.target.value)}
          onBlur={() => saveFindings(item.key, index)}
          placeholder={index === 0 ? 'No deficiency observed at time of inspection, or the repair required' : 'Repair required'} />
        <div style={styles.findingsFoot}>
          {index === 0 && !value.trim() && !hasRepair && (
            <button type="button" style={styles.chip} onClick={() => saveFindings(item.key, 0, NO_DEFICIENCY_TEXT)}>{NO_DEFICIENCY_TEXT}</button>
          )}
          {index === 0 && hasRepair && (!value.trim() || /no deficienc(y|ies) observed/i.test(value)) && (
            <span style={styles.warnText}>A repair is marked. Describe it here.</span>
          )}
          {value.length > LONG_FINDINGS && <span style={styles.warnText}>Long text may push the next photo off the page. Consider an additional repair photo.</span>}
        </div>
      </div>
    );
  };

  const renderStatusLines = (item, slot) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      {item.lines.map(line => {
        const value = slot.statuses?.[line.id];
        const ck = `${item.key}:c${line.id}`;
        return (
          <div key={line.id} style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
            <div style={styles.lineRow}>
              <span style={styles.lineLabel}>{line.label}</span>
              <div role="group" aria-label={line.label} style={styles.btnRow}>
                {line.opts.map((opt, i) => {
                  const sel = value === opt;
                  return (
                    <button key={opt} type="button" aria-pressed={sel}
                      style={sel ? (isPass(opt) ? styles.choiceOnPass : styles.choiceOnFail) : styles.choiceOff}
                      onClick={() => commit(item.key, autoFindings(setStatus(slot, line.id, opt), item))}>{line.lbls[i]}</button>
                  );
                })}
              </div>
            </div>
            {item.perLineComments && (
              <input style={styles.textInput} aria-label={`${line.label} comment`}
                value={draftVal(ck, slot.lineComments?.[line.id] || '')}
                onChange={e => typeDraft(ck, e.target.value)}
                onBlur={() => saveLineComment(item.key, line.id)}
                placeholder="Comment" />
            )}
          </div>
        );
      })}
    </div>
  );

  // ─── Item-specific fields (service size, panel table, …) ────────────────

  const saveDetailDraft = async (itemKey, key) => {
    const k = `${itemKey}:d${key}`;
    const text = drafts[k];
    if (text === undefined) return;
    const current = slotOf(itemKey);
    if ((current.details?.[key] || '') !== text) await commit(itemKey, setDetail(current, key, text));
    dropDraft(k);
  };

  const renderDetails = (item, slot) => {
    if (!item.details?.length && !item.panelTable) return null;
    const d = slot.details || {};
    const panels = Array.isArray(d.panels) ? d.panels : [];
    const savePanels = (next) => commit(item.key, autoFindings(setDetail(slot, 'panels', next), item));
    const panelDraftKey = (id, field) => `${item.key}:p${id}:${field}`;

    return (
      <div style={styles.detailsBox}>
        <span style={styles.detailsTitle}>Form details</span>
        {item.details?.length > 0 && (
          <div style={styles.detailsGrid}>
            {item.details.map(f => {
              const k = `${item.key}:d${f.key}`;
              if (f.type === 'checkbox') {
                return (
                  <label key={f.key} style={styles.checkRow}>
                    <input type="checkbox" checked={!!d[f.key]}
                      onChange={e => commit(item.key, setDetail(slot, f.key, e.target.checked))} />
                    <span>{f.label}</span>
                  </label>
                );
              }
              if (f.type === 'select') {
                return (
                  <label key={f.key} style={styles.smallLabel}>
                    {f.label}
                    <select style={styles.textInput} value={d[f.key] || ''}
                      onChange={e => commit(item.key, setDetail(slot, f.key, e.target.value))}>
                      <option value="">— Select —</option>
                      {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </label>
                );
              }
              return (
                <label key={f.key} style={styles.smallLabel}>
                  {f.label}
                  <input style={styles.textInput} value={draftVal(k, d[f.key] || '')} placeholder={f.placeholder || ''}
                    onChange={e => typeDraft(k, e.target.value)} onBlur={() => saveDetailDraft(item.key, f.key)} />
                </label>
              );
            })}
          </div>
        )}

        {item.panelTable && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <span style={styles.findingsLabel}>Panels</span>
            {panels.map((pnl, idx) => (
              <div key={pnl.id} style={styles.panelRow}>
                <span style={styles.panelNum}>{idx + 1}</span>
                {((county || '').toLowerCase().includes('boca') ? ['name'] : ['name', 'location']).map(field => {
                  const k = panelDraftKey(pnl.id, field);
                  return (
                    <input key={field} style={{ ...styles.textInput, flex: '1 1 160px' }}
                      aria-label={field === 'name' ? 'Panel name / ID' : 'Location'}
                      placeholder={field === 'name' ? 'Panel name / ID (e.g. MDP)' : 'Location (e.g. 2nd floor)'}
                      value={draftVal(k, pnl[field] || '')}
                      onChange={e => typeDraft(k, e.target.value)}
                      onBlur={() => {
                        const text = drafts[k];
                        if (text === undefined) return;
                        if ((pnl[field] || '') !== text) savePanels(panels.map(p => (p.id === pnl.id ? { ...p, [field]: text } : p)));
                        dropDraft(k);
                      }} />
                  );
                })}
                <div role="group" aria-label={`Panel ${idx + 1} condition`} style={styles.btnRow}>
                  {[['good', 'Good'], ['repair', 'Needs Repair']].map(([val, lbl]) => {
                    const sel = pnl.condition === val;
                    return (
                      <button key={val} type="button" aria-pressed={sel}
                        style={sel ? (val === 'good' ? styles.choiceOnPass : styles.choiceOnFail) : styles.choiceOff}
                        onClick={() => savePanels(panels.map(p => (p.id === pnl.id ? { ...p, condition: sel ? '' : val } : p)))}>{lbl}</button>
                    );
                  })}
                </div>
                <button type="button" style={styles.removeBtn} onClick={() => savePanels(panels.filter(p => p.id !== pnl.id))}>Remove</button>
              </div>
            ))}
            <button type="button" style={{ ...styles.secondaryBtn, alignSelf: 'flex-start' }}
              onClick={() => savePanels([...panels, { id: Date.now(), name: '', location: '', condition: '' }])}>+ Add panel</button>
          </div>
        )}
      </div>
    );
  };

  // ─── Slot card ──────────────────────────────────────────────────────────

  const renderSlot = (item) => {
    const isLead   = item.findings === false;
    const slot     = slotOf(item.key);
    const na       = isNotApplicable(slot);
    const fromList = na && slot.naSource === 'checklist';
    const confirmed = isConfirmed(slot);
    const hasRepair = !isLead && slotHasRepair(slot, item);
    // (findings warnings below use hasRepair; the confirm check uses slotProblems)
    const problems = slotProblems(slot, item);
    const naKey    = `${item.key}:na`;

    return (
      <section key={item.key} id={item.key}
        style={{ ...styles.slotCard, ...(highlight === item.key ? styles.slotHighlight : {}) }}
        aria-label={`${item.photoLabel} ${item.title}`}>
        <div style={styles.slotHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
            <span style={styles.photoNumBadge}>{item.photoLabel}</span>
            <h3 style={styles.slotTitle}>{item.title}</h3>
          </div>
          <div style={styles.btnRow}>
            {confirmed && <span style={{ ...styles.pill, ...styles.pillConfirmed }}>Confirmed</span>}
            {slot.review === 'ai' && <span style={{ ...styles.pill, ...styles.pillAi }}>AI suggestion</span>}
            {slot.review === 'edited' && <span style={{ ...styles.pill, ...styles.pillEdited }}>Edited — not confirmed</span>}
            {fromList && <span style={{ ...styles.pill, ...styles.pillNa }}>N/A from site systems</span>}
          </div>
        </div>

        {slot.aiNote && !na && <div style={styles.flag}>{slot.aiNote}</div>}

        {!isLead && !fromList && (
          <label style={styles.naRow}>
            <input type="checkbox" checked={na}
              onChange={e => commit(item.key, setNotApplicable(slot, e.target.checked ? 'not present at this property' : '', 'engineer'))} />
            <span>Not applicable</span>
          </label>
        )}

        {na ? (
          <div style={styles.slotBody}>
            <div style={styles.naPanel}>NOT APPLICABLE</div>
            <div style={styles.findingsWrap}>
              <label style={styles.findingsLabel} htmlFor={`na-${item.key}`}>
                Reason{fromList ? ' (change on Site systems)' : ''}
              </label>
              <input id={`na-${item.key}`} style={styles.textInput} disabled={fromList}
                value={draftVal(naKey, slot.naReason)}
                onChange={e => typeDraft(naKey, e.target.value)}
                onBlur={() => saveNAReason(item.key)} />
              <div style={styles.preview}><strong>Prints:</strong> {combinedFindings(slot, item)}</div>
            </div>
          </div>
        ) : (
          <>
            <div style={styles.slotBody}>
              {item.noPhoto ? (
                <div style={styles.referPanel}>REFER TO SEPARATE THERMOGRAPHY REPORT</div>
              ) : renderPhotoBox({
                photo: photoById(slot.photos[0]?.photoId),
                label: item.photoLabel,
                target: { itemKey: item.key, mode: 'primary' },
                onRemove: () => commit(item.key, removePhotoAt(slot, 0)),
              })}
              {!isLead && !item.perLineComments && renderFindings(item, 0, hasRepair)}
              {!isLead && item.perLineComments && (
                <div style={styles.findingsWrap}>
                  <span style={styles.findingsLabel}>Findings (built from the four line comments below)</span>
                  <div style={styles.preview}>{combinedFindings(slot, item) || 'No comments entered yet.'}</div>
                </div>
              )}
            </div>

            {!isLead && slot.photos.slice(1).map((p, i) => {
              const index = i + 1;
              const label = `${repairLabel(item.key, i)} ${repairCaption(item)}`;
              return (
                <div key={`${item.key}-x${index}`} style={styles.extraRow}>
                  <span style={styles.extraBadge}>{label}</span>
                  <div style={styles.slotBody}>
                    {renderPhotoBox({
                      photo: photoById(p.photoId),
                      label,
                      target: { itemKey: item.key, mode: 'extra' },
                      onRemove: () => commit(item.key, removePhotoAt(slot, index)),
                    })}
                    {!item.perLineComments && renderFindings(item, index, true)}
                  </div>
                </div>
              );
            })}

            {!isLead && renderStatusLines(item, slot)}

            {!isLead && renderDetails(item, slot)}

            {!isLead && !item.noPhoto && slot.photos.length > 1 && !item.perLineComments && (
              <div style={styles.preview}><strong>Form comment (all boxes joined):</strong> {combinedFindings(slot, item)}</div>
            )}
          </>
        )}

        <div style={styles.slotFoot}>
          <div style={styles.btnRow}>
            {!isLead && !na && !item.noPhoto && (
              <>
                <button type="button" style={styles.secondaryBtn}
                  onClick={() => setPicker({ itemKey: item.key, mode: 'extra', label: `Repair photo — ${repairCaption(item)}` })}>
                  + Additional repair photo
                </button>
                <label style={styles.secondaryUpload}>
                  + Upload repair photo
                  <input type="file" accept="image/*" style={{ display: 'none' }}
                    onChange={e => { uploadToSlot(e.target.files[0], { itemKey: item.key, mode: 'extra' }); e.target.value = ''; }} />
                </label>
              </>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {problems.length > 0 && (
              <span style={styles.warnText}>{confirmed ? 'Confirmed, but needs attention: ' : ''}{problems.join(' ')}</span>
            )}
            {!confirmed && (
              <button type="button"
                style={problems.length === 0 ? styles.confirmBtn : styles.disabledBtn}
                disabled={problems.length > 0}
                onClick={() => commit(item.key, confirmSlot(slot, currentUser.uid))}>Confirm</button>
            )}
          </div>
        </div>
      </section>
    );
  };

  // ─── Render ─────────────────────────────────────────────────────────────

  if (loading) return <div style={{ marginLeft: '220px', padding: '2rem' }}>Loading...</div>;

  return (
    <div>
      <Sidebar />
      {picker && renderPicker()}

      <div style={styles.container}>
        <div style={styles.titleRow}>
          <div>
            <h2 style={styles.title}>Report Editor</h2>
            {project && (
              <p style={styles.subtitle}>{project.address}{project.city ? `, ${project.city}` : ''} — {project.county}</p>
            )}
          </div>
          {screen === 'slots' && (
            <div style={styles.btnRow}>
              {systems.length > 0 && (
                <button type="button" style={styles.secondaryBtnLg} onClick={() => setScreen('checklist')}>Site systems</button>
              )}
              <button type="button" style={styles.secondaryBtnLg} onClick={() => navigate(`/project/${projectId}/electrical`)}>Project Details</button>
            </div>
          )}
        </div>

        {saveError && <div role="alert" style={styles.errorBanner}>{saveError}</div>}

        {uploading && (
          <div style={styles.uploadProgressBar}>
            <div style={{ ...styles.uploadProgressFill, width: `${uploadProgress}%` }} />
            <span style={styles.uploadProgressText}>Uploading... {uploadProgress}%</span>
          </div>
        )}

        {screen === 'checklist' ? renderChecklist() : (
          <div style={styles.slotList}>
            <section aria-label="Progress" style={styles.progressCard}>
              <div style={{ display: 'flex', gap: '1.75rem', flexWrap: 'wrap' }}>
                <div style={styles.stat}><span style={styles.statNum}>{confirmedCount} / {allItems.length}</span><span style={styles.statLabel}>Slots confirmed</span></div>
                <div style={styles.stat}><span style={{ ...styles.statNum, color: '#1d4ed8' }}>{aiCount}</span><span style={styles.statLabel}>AI suggestions to review</span></div>
                <div style={styles.stat}><span style={styles.statNum}>{unusedCount}</span><span style={styles.statLabel}>Photos not used</span></div>
              </div>
              {openLabels.length === 0
                ? <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#065f46' }}>All slots confirmed — Electrical Form is ready</span>
                : <span style={{ fontSize: '0.8rem', color: '#4b5563', maxWidth: '420px' }}>Report generation unlocks when every slot is confirmed. Open: {openLabels.join(', ')}</span>}
            </section>

            {allItems.map(renderSlot)}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const choiceBase = { minHeight: '40px', padding: '0 0.9rem', borderRadius: '8px', fontSize: '0.85rem', cursor: 'pointer', fontFamily: 'inherit' };

const styles = {
  container:          { marginLeft: '200px', padding: '2rem', paddingBottom: '5rem' },
  titleRow:           { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.25rem', gap: '1rem', flexWrap: 'wrap' },
  title:              { margin: '0 0 0.25rem', color: '#112b49', fontSize: '1.5rem' },
  subtitle:           { margin: 0, color: '#555', fontSize: '0.9rem' },
  errorBanner:        { backgroundColor: '#fee2e2', border: '1px solid #fca5a5', color: '#7f1d1d', borderRadius: '6px', padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.875rem' },
  uploadProgressBar:  { position: 'relative', height: '28px', backgroundColor: '#e5e7eb', borderRadius: '4px', marginBottom: '1rem', overflow: 'hidden' },
  uploadProgressFill: { position: 'absolute', top: 0, left: 0, height: '100%', backgroundColor: 'cornflowerblue', transition: 'width 0.2s' },
  uploadProgressText: { position: 'absolute', width: '100%', textAlign: 'center', lineHeight: '28px', fontSize: '0.8rem', fontWeight: '600', color: '#112b49' },

  card:               { backgroundColor: 'rgba(255,255,255,0.96)', borderRadius: '10px', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', display: 'flex', flexDirection: 'column', gap: '1rem' },
  cardTitle:          { margin: 0, fontSize: '1.15rem', color: '#112b49' },
  cardText:           { margin: 0, fontSize: '0.9rem', color: '#374151', lineHeight: 1.5, maxWidth: '640px' },
  sysRow:             { display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid #e5e7eb', paddingTop: '0.9rem' },
  sysTitle:           { fontSize: '0.95rem', fontWeight: 600, color: '#112b49' },
  smallLabel:         { display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.75rem', color: '#4b5563' },
  printsLine:         { fontSize: '0.78rem', color: '#374151' },
  checklistFoot:      { display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '0.75rem', borderTop: '1px solid #e5e7eb', paddingTop: '1rem', flexWrap: 'wrap' },

  progressCard:       { backgroundColor: 'rgba(255,255,255,0.96)', borderRadius: '10px', padding: '1rem 1.25rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'center', justifyContent: 'space-between' },
  stat:               { display: 'flex', flexDirection: 'column' },
  statNum:            { fontSize: '1.5rem', fontWeight: 700, color: '#112b49', lineHeight: 1.1 },
  statLabel:          { fontSize: '0.75rem', color: '#4b5563' },

  slotList:           { display: 'flex', flexDirection: 'column', gap: '1rem' },
  slotCard:           { backgroundColor: 'rgba(255,255,255,0.96)', borderRadius: '10px', padding: '1.25rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', display: 'flex', flexDirection: 'column', gap: '0.85rem' },
  slotHighlight:      { boxShadow: '0 0 0 3px #6495ED', transition: 'box-shadow 0.3s' },
  slotHeader:         { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' },
  slotTitle:          { margin: 0, fontSize: '1rem', color: '#112b49', fontWeight: 600 },
  photoNumBadge:      { backgroundColor: '#112b49', color: 'white', fontSize: '0.75rem', fontWeight: '700', padding: '0.2rem 0.55rem', borderRadius: '3px', letterSpacing: '0.05em' },
  extraBadge:         { alignSelf: 'flex-start', backgroundColor: '#7f1d1d', color: 'white', fontSize: '0.72rem', fontWeight: '700', padding: '0.15rem 0.5rem', borderRadius: '3px', letterSpacing: '0.05em' },
  pill:               { fontSize: '0.75rem', fontWeight: 600, borderRadius: '999px', padding: '0.25rem 0.65rem' },
  pillConfirmed:      { backgroundColor: '#dcfce7', color: '#14532d' },
  pillAi:             { backgroundColor: '#dbeafe', color: '#1e3a8a' },
  pillEdited:         { backgroundColor: '#ffedd5', color: '#7c2d12' },
  pillNa:             { backgroundColor: '#fef3c7', color: '#78350f' },
  flag:               { fontSize: '0.85rem', color: '#7c2d12', backgroundColor: '#fff7ed', border: '1px solid #fdba74', borderRadius: '6px', padding: '0.5rem 0.75rem' },
  naRow:              { display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', color: '#374151', cursor: 'pointer', alignSelf: 'flex-start' },

  slotBody:           { display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-start' },
  photoBox:           { flex: '0 1 320px', display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  emptyPhoto:         { flex: '0 1 320px', border: '2px dashed #c3cad5', borderRadius: '6px', padding: '1.5rem 1rem', display: 'flex', justifyContent: 'center', backgroundColor: '#f9fafb', minHeight: '120px', alignItems: 'center', boxSizing: 'border-box' },
  thumbnail:          { width: '100%', height: 'auto', maxHeight: '240px', objectFit: 'contain', borderRadius: '4px', border: '1px solid #e5e7eb', backgroundColor: '#f3f4f6' },
  naPanel:            { flex: '0 1 320px', height: '150px', border: '1px solid #9ca3af', backgroundColor: '#eceef1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, letterSpacing: '0.08em', color: '#374151', borderRadius: '4px' },
  referPanel:         { flex: '0 1 320px', height: '150px', border: '1px solid #9ca3af', backgroundColor: '#eceef1', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '0 1rem', fontWeight: 700, letterSpacing: '0.04em', color: '#374151', borderRadius: '4px', fontSize: '0.85rem' },
  findingsWrap:       { flex: '1 1 320px', display: 'flex', flexDirection: 'column', gap: '0.35rem', minWidth: 0 },
  findingsLabel:      { fontSize: '0.8rem', fontWeight: 600, color: '#374151' },
  findings:           { border: '1px solid #c3cad5', borderRadius: '6px', padding: '0.6rem 0.75rem', fontSize: '0.9rem', fontFamily: 'inherit', lineHeight: 1.45, resize: 'vertical' },
  findingsFoot:       { display: 'flex', flexDirection: 'column', gap: '0.3rem', alignItems: 'flex-start' },
  textInput:          { padding: '0.5rem 0.75rem', border: '1px solid #c3cad5', borderRadius: '6px', fontSize: '0.9rem', fontFamily: 'inherit' },
  chip:               { backgroundColor: '#e0e7ff', color: '#312e81', border: 'none', borderRadius: '999px', padding: '0.3rem 0.75rem', fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'inherit' },
  warnText:           { fontSize: '0.78rem', color: '#7f1d1d' },
  preview:            { fontSize: '0.82rem', color: '#374151', backgroundColor: '#f6f8fb', borderRadius: '6px', padding: '0.55rem 0.75rem', lineHeight: 1.45 },
  extraRow:           { display: 'flex', flexDirection: 'column', gap: '0.5rem', borderTop: '1px dashed #d1d5db', paddingTop: '0.75rem' },
  lineRow:            { display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' },
  lineLabel:          { fontSize: '0.82rem', color: '#374151', minWidth: '170px' },
  slotFoot:           { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', borderTop: '1px solid #eef0f3', paddingTop: '0.75rem' },

  btnRow:             { display: 'flex', gap: '0.5rem', flexWrap: 'wrap' },
  choiceOff:          { ...choiceBase, border: '1px solid #c3cad5', backgroundColor: '#ffffff', color: '#374151' },
  choiceOnPass:       { ...choiceBase, border: '2px solid #047857', backgroundColor: '#d1fae5', color: '#064e3b', fontWeight: 600 },
  choiceOnFail:       { ...choiceBase, border: '2px solid #b91c1c', backgroundColor: '#fee2e2', color: '#7f1d1d', fontWeight: 600 },
  choiceOnNa:         { ...choiceBase, border: '2px solid #b45309', backgroundColor: '#fef3c7', color: '#78350f', fontWeight: 600 },
  primaryBtn:         { padding: '0.5rem 1rem', backgroundColor: '#112b49', color: 'white', border: 'none', borderRadius: '4px', fontSize: '0.85rem', cursor: 'pointer', fontFamily: 'inherit', minHeight: '36px' },
  primaryBtnLg:       { minHeight: '44px', padding: '0 1.4rem', backgroundColor: '#112b49', color: 'white', border: 'none', borderRadius: '8px', fontSize: '0.95rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  disabledBtnLg:      { minHeight: '44px', padding: '0 1.4rem', backgroundColor: '#e5e9ef', color: '#4b5563', border: '1px solid #c3cad5', borderRadius: '8px', fontSize: '0.95rem', fontWeight: 600, cursor: 'not-allowed', fontFamily: 'inherit' },
  uploadLabel:        { display: 'inline-flex', alignItems: 'center', padding: '0.5rem 1rem', backgroundColor: 'cornflowerblue', color: 'white', borderRadius: '4px', fontSize: '0.85rem', cursor: 'pointer', fontFamily: 'inherit', minHeight: '36px', boxSizing: 'border-box' },
  secondaryBtn:       { padding: '0.4rem 0.85rem', backgroundColor: '#ffffff', color: '#112b49', border: '1px solid #112b49', borderRadius: '4px', fontSize: '0.8rem', cursor: 'pointer', fontFamily: 'inherit', minHeight: '34px' },
  secondaryBtnLg:     { minHeight: '44px', padding: '0 1.1rem', backgroundColor: '#ffffff', color: '#112b49', border: '1px solid #112b49', borderRadius: '8px', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  secondaryUpload:    { display: 'inline-flex', alignItems: 'center', padding: '0.4rem 0.85rem', backgroundColor: '#ffffff', color: '#112b49', border: '1px solid #112b49', borderRadius: '4px', fontSize: '0.8rem', cursor: 'pointer', fontFamily: 'inherit', minHeight: '34px', boxSizing: 'border-box' },
  removeBtn:          { padding: '0.4rem 0.85rem', backgroundColor: '#fee2e2', color: '#7f1d1d', border: 'none', borderRadius: '4px', fontSize: '0.8rem', cursor: 'pointer', fontFamily: 'inherit', minHeight: '34px' },
  confirmBtn:         { minHeight: '44px', padding: '0 1.25rem', backgroundColor: '#112b49', color: 'white', border: 'none', borderRadius: '8px', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  disabledBtn:        { minHeight: '44px', padding: '0 1.25rem', backgroundColor: '#e5e9ef', color: '#4b5563', border: '1px solid #c3cad5', borderRadius: '8px', fontSize: '0.9rem', fontWeight: 600, cursor: 'not-allowed', fontFamily: 'inherit' },

  detailsBox:         { display: 'flex', flexDirection: 'column', gap: '0.6rem', backgroundColor: '#f6f8fb', borderRadius: '8px', padding: '0.75rem 0.9rem' },
  detailsTitle:       { fontSize: '0.78rem', fontWeight: 700, color: '#112b49', textTransform: 'uppercase', letterSpacing: '0.05em' },
  detailsGrid:        { display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'flex-end' },
  checkRow:           { display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', color: '#374151', minHeight: '36px' },
  panelRow:           { display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center', backgroundColor: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '0.5rem' },
  panelNum:           { fontSize: '0.8rem', fontWeight: 700, color: '#112b49', minWidth: '1.25rem' },
  modalOverlay:       { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modalBox:           { backgroundColor: 'white', borderRadius: '12px', width: '90%', maxWidth: '900px', height: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  modalHeader:        { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.25rem', borderBottom: '1px solid #e5e7eb' },
  modalTitle:         { fontWeight: '600', color: '#112b49', fontSize: '1rem' },
  modalClose:         { background: 'none', border: 'none', fontSize: '1.1rem', cursor: 'pointer', color: '#4b5563', minWidth: '36px', minHeight: '36px' },
  modalEmpty:         { padding: '2rem', textAlign: 'center', color: '#4b5563' },
  pickerGrid:         { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '1rem', padding: '1rem' },
  pickerCard:         { border: '2px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden', cursor: 'pointer', display: 'flex', flexDirection: 'column', backgroundColor: '#000', padding: 0, textAlign: 'left', fontFamily: 'inherit' },
  pickerThumb:        { width: '100%', height: '260px', objectFit: 'contain', display: 'block' },
  pickerCaption:      { padding: '0.45rem 0.6rem', display: 'flex', flexDirection: 'column', gap: '2px', backgroundColor: '#fafafa', fontSize: '0.75rem' },
  confirmBanner:      { backgroundColor: '#fffbeb', border: '1px solid #fbbf24', borderRadius: '6px', padding: '0.75rem 1rem', margin: '0.75rem 1rem', fontSize: '0.875rem', color: '#78350f' },
  confirmYes:         { padding: '0.4rem 0.9rem', backgroundColor: '#b45309', color: 'white', border: 'none', borderRadius: '4px', fontSize: '0.8rem', cursor: 'pointer', fontFamily: 'inherit' },
  linkText:           { color: 'cornflowerblue', textDecoration: 'underline', cursor: 'pointer' },
  confirmNo:          { padding: '0.4rem 0.9rem', backgroundColor: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '4px', fontSize: '0.8rem', cursor: 'pointer', fontFamily: 'inherit' },
};

export default PhotoManager;
